import { DiscoveryLevel, FeatureType, GetCreatorProfileResponse, GetFolderResponse, GetTopicResponse, TopicSearchResult, UserType } from '../../types';
import { LitElement, css, html } from 'lit';
import { addTopicToFolder, removeTopicFromFolder } from '../../helpers/api/folders-api';
import { customElement, property, query, state } from 'lit/decorators';
import { deleteTopic, getTopic } from '../../helpers/api/topics-api';
import { isDefined, undefinedIfEmpty } from '../../helpers/utils';
import { swapUrlForCustomDomain } from '../../helpers/urls';

import { Dialog } from '@material/mwc-dialog';
import { SettingDrawer } from '../mobile/setting-drawer';
import accountInfo from '../../helpers/state/account-info';
import { detectMobileViewport } from '../../helpers/device';
import { getMainPage } from '../../helpers/page-name';
import { getSmallSocialImageForTopic } from '../../helpers/files';
import { ifDefined } from 'lit/directives/if-defined';
import { logWithAmplitude } from '../../helpers/api/api';
import { migrateTopicToOrganization } from '../../helpers/api/organization-api';
import organizationInfo from '../../helpers/state/organization-info';
import { palette } from '../../style/variables';

@customElement('backpack-card')
export class BackpackCard extends LitElement {
    private readonly amplitude_callsite: string = 'Backpack Card';

    @query('#deleteTopicModalComponent')
    deleteTopicModalComponent?: Dialog;

    @query('#duplicationMapComponent')
    duplicationModal!: Dialog;

    @query('#topicToFolderDrawer')
    topicToFolderDrawer?: SettingDrawer;

    @query('#folderStructureModal')
    folderStructureModal?: Dialog;


    @query('#topicToTeamModal')
    topicToTeamModal?: Dialog;


    @query('#topicToTeamDrawer')
    topicToTeamDrawer?: SettingDrawer;

    @query('#removeTopicFromFolderModal')
    removeTopicFromFolderModal!: Dialog;

    @query('#shareModal')
    shareModal!: Dialog;

    @query('#upgradeDialogComponent')
    upgradeDialogComponent!: Dialog;

    @property({ type: Object })
    topic: TopicSearchResult | undefined;

    @property({ type: Object })
    fullTopic: GetTopicResponse | undefined;

    @property({ type: Array })
    folders: GetFolderResponse[] = [];

    @property({ type: Object})
    creatorProfile?: GetCreatorProfileResponse;

    @property({ type: Boolean})
    isPinned: boolean = false;

    @property({ type: Boolean})
    isOnly: boolean = false;

    @property({ type: Boolean})
    canPin: boolean = false;

    @property({ type: Boolean })
    showRemoveFromFolder: boolean = false;

    @property({ type: Boolean })
    hideViewerType: boolean = false;

    @property({ type: Boolean })
    hideMetadata: boolean = false;

    @property({ type: Boolean })
    hideBookmarkButton: boolean = false;

    @property({ type: String })
    selectedFolderId?: string;

    @property({ type: String })
    selectedTeamId?: string;

    @property( {type: Boolean })
    goToDetailPage: boolean = false;

    @property( {type: Boolean })
    inCarousel: boolean = false;

    @property( {type: Boolean })
    hideActions: boolean = false;

    @property( {type: Boolean })
    onboarding: boolean = false;

    @property( {type: Boolean })
    loadModals: boolean = false;

    @property({ type: Boolean })
    mini: boolean = false;

    @state()
    topicUrl?: string;

    @state()
    crowdsourceLink?: string;

    @state()
    shareLink?: string;

    @state()
    viewonlyLink?: string;

    @state()
    folderInfo?: GetFolderResponse;

    @state()
    selectedFolderInfo?: GetFolderResponse;

    @state()
    showImage: boolean = false;

    @state()
    viewType?: string;

    @state()
    readKey?: string;

    @state()
    readLinkWithKey?: string;

    @state()
    backgroundImage?: string;

    userType: UserType = 'Viewer';

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('topic')){
            this.backgroundImage = isDefined(this.topic) ? getSmallSocialImageForTopic(this.topic) : '';
            this.checkForBookmarkKeys();
            this.setUserTypeAndTopicUrl();
        }
    }

    checkForBookmarkKeys() {
        if (this.topic?.is_owner === true || this.topic?.is_writer === true) {
            return;
        }
        let bookmark = accountInfo.bookmarks.find((bookmark) => bookmark.topic_id === this.topic?._id);
        for (let i = 0; i < accountInfo.bookmarks.length; i++) {
            if (bookmark?.topic_key !== undefined) {
                // Found a bookmark with a defined topic_key, break and move on
                break;
            }
            bookmark = accountInfo.bookmarks[i];
        }
        if (bookmark?.topic_key === undefined) {
            // None of the bookmarks have a defined topic_key, return
            return;
        }

        this.readKey = bookmark?.topic_key;
        if (this.topic?.discovery_level === DiscoveryLevel.Private) {
            this.readLinkWithKey = `/#command-center?topic_id=${this.topic?._id.$oid}&topic_key=${this.readKey}`;
        } else if (this.topic) {
            this.readLinkWithKey = `/#discover-details?topic_id=${this.topic?._id.$oid}&topic_key=${this.readKey}`;
        }
    }

    setUserTypeAndTopicUrl() {
        if(this.goToDetailPage) {
            this.topicUrl = this.topic?.discover_details_link ?? `/#discover-details?topic_id=${this.topic?._id.$oid ?? ''}`;
        } else if (this.topic?.is_owner === true) {
            this.userType = 'Owner';
            this.topicUrl = this.topic?.cc_account_admin_link;
        } else if (this.topic?.is_writer === true) {
            this.userType = 'Editor';
            this.topicUrl = this.topic?.cc_account_admin_link;
        } else if (this.topic?.is_addpointer === true) {
            this.userType = 'Collaborator';
            this.topicUrl = this.topic?.cc_addpoint_link;
        } else if (this.readKey) {
            this.userType = 'Viewer';
            this.topicUrl = this.readLinkWithKey;
        } else {
            this.userType = 'Viewer';
            this.topicUrl = this.topic?.discovery_level === DiscoveryLevel.Private ? this.topic?.cc_keyless_link : this.topic?.discover_details_link;
        }

        // if the current base url is something other than the base url of this.topicUrl then replace the base url in this.topicUrl with the current base url
        if (this.topicUrl) {
            try {
                const currentBaseUrl = new URL(window.location.href);
                const topicUrl = new URL(this.topicUrl);
                if (currentBaseUrl.origin !== topicUrl.origin) {
                    const newUrl = new URL(
                        topicUrl.pathname + topicUrl.search + topicUrl.hash,
                        currentBaseUrl.origin
                    );
                    this.topicUrl = newUrl.toString();
                }
            } catch (error) {
                console.error(`Invalid URL: ${this.topicUrl}`, error);
            }
        }
    }

    private triggerDeleteTopic() {
        if (this.deleteTopicModalComponent) {
            this.deleteTopicModalComponent.open = true;
        }
    }

    private async deleteTopic() {
        if (this.topic?._id) {
            await deleteTopic(this.amplitude_callsite, this.topic._id.$oid);

            this.dispatchEvent(new CustomEvent('on-delete-topic', {
                detail: {
                    deletedTopicId: this.topic._id.$oid,
                }
            }));
        }

        this.deleteTopicModalComponent?.close();
    }

    private async toggleAddToFolder() {
        if (detectMobileViewport()) {
            await this.topicToFolderDrawer?.open();
            this.folderStructureModal?.close();
        } else if(this.folderStructureModal) {
            this.folderStructureModal.open = true;
        }
    }

    private async toggleDuplicateTopic() {
        if (this.topic?._id) {
            this.fullTopic = await getTopic(this.amplitude_callsite, this.topic?._id.$oid);
            this.duplicationModal.open = true;
        }
    }

    private async toggleShareTopic() {
        if (this.topic?.is_owner === true|| this.topic?.is_writer === true) {
            if (this.topic?.is_addpointer) {
                if (this.topic?.discovery_level === 'addpoint_public') {
                    this.crowdsourceLink = undefinedIfEmpty(this.topic?.cc_keyless_link);
                } else {
                    this.crowdsourceLink = undefinedIfEmpty(this.topic?.cc_addpoint_link);
                }
            }
            if (this.topic?.discovery_level !== 'private') {
                this.viewonlyLink = undefinedIfEmpty(this.topic?.cc_keyless_link);
            } else {
                this.viewonlyLink = undefinedIfEmpty(this.topic?.cc_read_link);
            }
        } else {
            if (this.topic?.discovery_level === 'private') {
                if (this.topic?.is_addpointer) {
                    this.shareLink = this.topic.cc_addpoint_link;
                    logWithAmplitude(
                        this.amplitude_callsite,
                        `Share Collaborator Map Button Click: ${window.location.hash.split('?')[0]}`
                    );
                } else {
                    this.shareLink = this.topic?.cc_read_link;
                    logWithAmplitude(
                        this.amplitude_callsite,
                        `Share Read Map Button Click: ${window.location.hash.split('?')[0]}`
                    );
                }
            } else if (this.topic?.discovery_level === 'read_public') {
                if (this.topic?.is_addpointer) {
                    this.shareLink = this.topic.cc_addpoint_link;
                    logWithAmplitude(
                        this.amplitude_callsite,
                        `Share Collaborator Map Button Click: ${window.location.hash.split('?')[0]}`
                    );
                } else {
                    this.shareLink = this.topic?.cc_keyless_link;
                    logWithAmplitude(
                        this.amplitude_callsite,
                        `Share Keyless Map Button Click: ${window.location.hash.split('?')[0]}`
                    );
                }
            } else {
                this.shareLink = this.topic?.cc_keyless_link;
            }
        }

        // Swap .proxi.co links to the custom domain if one is verified
        if (this.shareLink) this.shareLink = swapUrlForCustomDomain(this.shareLink);
        if (this.crowdsourceLink) this.crowdsourceLink = swapUrlForCustomDomain(this.crowdsourceLink);
        if (this.viewonlyLink) this.viewonlyLink = swapUrlForCustomDomain(this.viewonlyLink);

        this.shareModal.open = true;
    }

    private pinMap() {
        this.dispatchEvent(new CustomEvent('on-pin-topic'));
    }

    private toggleRemoveFromFolder() {
        this.removeTopicFromFolderModal.open = true;
    }

    private onMenuOpen() {
        this.loadModals = true;
        this.requestUpdate();
    }

    private renderTopLeftCTA() {
        if (isDefined(this.creatorProfile)) {
            if (this.canPin) {
                return html`<form-button size="xs" class="${this.isPinned ? 'backpack-card__action--remove-pin' : 'backpack-card__action--add-pin'}" @click="${this.pinMap}" icon="push_pin" raised>${this.isPinned ? 'Remove Pin' : 'Pin'}</form-button>`;
            }
            if (this.isPinned) {
                return html`<mwc-icon class="backpack-card__pin">push_pin</mwc-icon>`;
            }
        } else {
            if (this.hideViewerType) {
                return html`<div></div>`;
            }
            else {
                return html`<div class="backpack-card__user-type backpack-card__user-type--${this.userType}">${this.userType}</div>`;
            }
        }
    }

    private removeFromCreatorProfile() {
        this.dispatchEvent(new CustomEvent('on-remove-from-creator-profile'));
    }

    private async submitAddToFolder() {
        if (this.selectedFolderId && this.topic != null) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (this.selectedFolderId && this.topic?._id) {
                    await addTopicToFolder(
                        this.amplitude_callsite,
                        this.selectedFolderId,
                        this.topic?._id.$oid,
                        {}
                    );
                }
                this.folderStructureModal?.close();
                await this.topicToFolderDrawer?.close();
            }, 'An error has occurred.  Context: Failed to add to folder');
        }
    }

    private async submitAddToTeam() {
        if (isDefined(this.topic) && isDefined(this.selectedTeamId)) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                await migrateTopicToOrganization(this.amplitude_callsite, this.selectedTeamId ?? '', this.topic?._id.$oid ?? '');
                await this.topicToTeamDrawer?.close();
                this.topicToTeamModal?.close();
                this.dispatchEvent(new CustomEvent('on-move-topic', {
                        detail: {
                            movedTopicId: this.topic?._id.$oid,
                        }
                    })
                );
            }, 'An error has occurred.  Context: Failed to add map to team. You may not have the right accesses on the map or the team.');
        }
    }

    private async addToTeam() {
        if (detectMobileViewport()) {
            await this.topicToTeamDrawer?.open();
        } else if(this.topicToTeamModal) {
            this.topicToTeamModal.open = true;
        }
    }

    private renderTopRightCTA() {
        if (isDefined(this.creatorProfile)) {
            if (!this.creatorProfile.is_owner && !this.hideBookmarkButton) {
                return html`<bookmark-map-button .topic=${this.topic}></bookmark-map-button>`;
            } else if (this.canPin) {
                return html`<form-button  backgroundColor="${palette.failureRed}" size="xs" class="backpack-card__action--remove" @click="${this.removeFromCreatorProfile}" icon="remove_circle_outline" raised>Remove</form-button>`;
            }
        } else if (this.topic?.is_owner === true|| this.topic?.is_writer === true) {
            return html`
                <topic-menu
                    ?isOwner="${this.topic?.is_owner}"
                    ?isWriter="${this.topic?.is_writer}"
                    .showRemoveFromFolder=${this.showRemoveFromFolder}
                    .folders=${this.folders}
                    @open-menu=${this.onMenuOpen}
                    @duplicate-topic=${async () => await this.toggleDuplicateTopic()}
                    @share-topic=${async () => await this.toggleShareTopic()}
                    @move-topic=${this.addToTeam}
                    @add-to-folder=${this.toggleAddToFolder}
                    @delete-topic=${() => this.triggerDeleteTopic()}
                    @remove-from-folder=${() => this.toggleRemoveFromFolder()}>
                </topic-menu>`;
        } else {
            if (this.hideBookmarkButton) {
                return html``;
            }
            return html`<bookmark-map-button .topic=${this.topic}></bookmark-map-button>`;
        }
    }

    private async removeTopic() {
        if (isDefined(this.topic) && isDefined(this.selectedFolderId)) {
            await removeTopicFromFolder(
                this.amplitude_callsite,
                this.selectedFolderId,
                this.topic?._id.$oid,
                {}
            );

            this.dispatchEvent(new CustomEvent('on-remove-topic-from-folder', {
                detail: {
                    removedTopicId: this.topic._id.$oid,
                }
            }));

            this.removeTopicFromFolderModal.close();
        }
    }


    private selectFolderToAdd(e: any) {
        this.selectedFolderId = e.detail.folderId;
    }


    private selectTeamToAdd(e: any) {
        this.selectedTeamId = e.detail.teamId;
    }

    private openUpgradeDialog() {
        this.upgradeDialogComponent.open = true;
        this.shareModal?.close();
    }

    protected render() {
        return html`
            ${this.viewType !== 'list' ? html`
                <div class="backpack-card__wrapper ${this.inCarousel ? 'backpack-card__wrapper--scroll' : ''} ${this.isOnly ? 'backpack-card__wrapper--only' : ''}">
                    ${this.hideActions ? '' : html`
                        <span class="backpack-card__actions">
                            ${this.renderTopLeftCTA()}
                            <a href="${ifDefined(this.topicUrl)}" target="_blank" style="flex:1">
                            </a>
                            ${this.renderTopRightCTA()}
                        </span>
                    `}
                    <a href="${ifDefined(this.topicUrl)}" target="_blank">
                        <div class="backpack-card__image-wrapper">
                            <img src="/images/backpack/map_loading.png" class="backpack-card__image" alt="card-img"/>
                            <img src="${ifDefined(this.backgroundImage)}" class="backpack-card__image" alt="card-img" onload="this.previousElementSibling.style.display='none'">
                        </div>
                    </a>
                    <a href="${ifDefined(this.topicUrl)}" target="_blank" class="backpack-card__info">
                        <div class="backpack-card__title-wrapper">
                            ${this.showImage ? html`<img class="backpack-card__avatar" src="/images/creatorimgdefault.png"/>` : ''}
                            <p class="backpack-card__title ${this.onboarding || this.mini ? 'small-text' : ''}">${this.topic?.name ?? ''}</p>
                        </div>
                        ${this.hideMetadata ? '' : html`
                        <div class="backpack-card__metadata">
                            <span class="backpack-card__metadata-item">
                                <mwc-icon class="backpack-card__metadata-icon" outlined>place</mwc-icon>
                                ${this.topic?.statistics.approximate_point_count}
                            </span>
                            <span class="backpack-card__metadata-item">
                                <mwc-icon class="backpack-card__metadata-icon" outlined>visibility</mwc-icon>
                                ${Number(this.topic?.statistics.approximate_topic_views ?? 0) + Number(this.topic?.statistics.approximate_challenge_views ?? 0)}
                            </span>
                        </div>
                        `}
                    </a>
                </div>
            ` : ''}

            ${this.viewType === 'list' ? html`
                <div class="tr">
                    <div class="td">
                        ${this.topic?.name ?? ''}
                    </div>
                    <div class="td desktopOnly">
                        ${this.topic?.statistics.approximate_point_count}
                    </div>
                    <div class="td desktopOnly">
                        ${this.topic?.statistics.approximate_topic_views}
                    </div>
                    <div class="td desktopOnly">
                        ${this.topic?.statistics.approximate_challenge_views ?? 0}
                    </div>
                    <div class="td desktopOnly">
                        ${new Date(this.topic?.updated_at ?? '').toLocaleString()}
                    </div>
                    <div class="td td-actions">
                        <form-button
                            variant="outline"
                            size="sm"
                            @click="${() => window.open(this.topicUrl)}">
                            ${this.topic?.is_owner === true || this.topic?.is_writer === true ? html`
                                <mwc-icon class="btnIcon">edit</mwc-icon>
                                Edit
                            ` : html`
                                <mwc-icon class="btnIcon">visibility</mwc-icon>
                                View
                            `}

                        </form-button>
                        ${this.renderTopRightCTA()}
                    </div>
                </div>
            ` : ''}

            <error-toast id="errorToast"></error-toast>
            ${this.loadModals ? html`
                <mwc-dialog id="shareModal" class="dialog" hideActions>
                    <modal-dialog size="md" @close="${() => this.shareModal.close()}">
                        ${this.topic?.is_owner === true|| this.topic?.is_writer === true
                            ? html`<map-sharing-component
                                        slot="body"
                                        .topic="${this.topic}"
                                        .hideCurrentView= "${true}"
                                        .crowdsourceLink="${this.crowdsourceLink ?? ''}"
                                        .viewonlyLink="${this.viewonlyLink ?? ''}"
                                        .socialMediaShareImage="${this.backgroundImage}"
                                        .topicKey="${this.readKey ?? ''}"
                                        @open-upgrade-dialog=${this.openUpgradeDialog}>
                                    </map-sharing-component>`
                            : html`
                                    <map-sharing-component
                                        slot="body"
                                        .topic="${this.topic}"
                                        .hideCurrentView="${true}"
                                        .viewonlyLink="${this.shareLink ?? ''}"
                                        .topicKey="${this.readKey ?? ''}"
                                        .socialMediaShareImage="${this.backgroundImage}"
                                        >
                                    </map-sharing-component>
                                    `
                        }
                    </modal-dialog>
                </mwc-dialog>
                ${detectMobileViewport() ?
                    html`<setting-drawer id="topicToTeamDrawer">
                            <div style="padding: 0 1rem 1rem">
                                <team-selector
                                    ?isModal=${true}
                                    .alLTeams=${organizationInfo.allOrganizations}
                                    .currentTopicOrganizationId=${this.topic?.organization_id}
                                    @on-team-select=${this.selectTeamToAdd}>
                                </team-selector>
                                <div class="actionButtons">
                                    <form-button
                                        variant="social"
                                        @click="${async () => {
                                            await this.topicToTeamDrawer?.close();
                                        }}">
                                        Cancel
                                    </form-button>
                                    <form-button
                                        class="form-btn"
                                        ?disabled=${!this.selectedTeamId}
                                        @click="${this.submitAddToTeam}">
                                        Add
                                    </form-button>
                                </div>
                            </div>
                            </setting-drawer>` :
                    html`<mwc-dialog id="topicToTeamModal" class="dialog" hideActions>
                            <modal-dialog size="lg" @close="${() => this.topicToTeamModal?.close()}">
                                <div slot="body" style="padding: 0 1rem 1rem">
                                        <team-selector
                                            ?isModal=${true}
                                            .allTeams=${organizationInfo.allOrganizations}
                                            .currentTopicOrganizationId=${this.topic?.organization_id}
                                            @on-team-select=${this.selectTeamToAdd}>
                                        </team-selector>
                                        <div class="actionButtons">
                                        <form-button
                                            variant="social"
                                            @click="${() => this.topicToTeamModal?.close()}">
                                            Cancel
                                        </form-button>
                                        <form-button
                                            class="form-btn"
                                            ?disabled=${!this.selectedTeamId}
                                            @click="${this.submitAddToTeam}">
                                            Add
                                        </form-button>
                                    </div>
                                </div>
                            </modal-dialog>
                        </mwc-dialog>`
                }
                ${detectMobileViewport() ?
                    html`<setting-drawer id="topicToFolderDrawer">
                            <div style="padding: 0 1rem 1rem">
                                <folder-selector
                                    ?isModal=${true}
                                    .folders=${this.folders}
                                    @on-folder-select=${this.selectFolderToAdd}>
                                </folder-selector>
                                <div class="actionButtons">
                                    <form-button
                                        variant="social"
                                        @click="${async () => {
                                            this.folderStructureModal?.close();
                                            await this.topicToFolderDrawer?.close();
                                        }}">
                                        Cancel
                                    </form-button>
                                    <form-button
                                        class="form-btn"
                                        ?disabled=${!this.selectedFolderId}
                                        @click="${this.submitAddToFolder}">
                                        Add
                                    </form-button>
                                </div>
                            </div>
                            </setting-drawer>` :
                    html`<mwc-dialog id="folderStructureModal" class="dialog" hideActions>
                            <modal-dialog size="lg" @close="${() => this.folderStructureModal?.close()}">
                                <div slot="body" style="padding: 0 1rem 1rem">
                                        <folder-selector
                                            ?isModal=${true}
                                            .folders=${this.folders}
                                            @on-folder-select=${this.selectFolderToAdd}>
                                        </folder-selector>
                                        <div class="actionButtons">
                                        <form-button
                                            variant="social"
                                            @click="${() => this.folderStructureModal?.close()}">
                                            Cancel
                                        </form-button>
                                        <form-button
                                            class="form-btn"
                                            ?disabled=${!this.selectedFolderId}
                                            @click="${this.submitAddToFolder}">
                                            Add
                                        </form-button>
                                    </div>
                                </div>
                            </modal-dialog>
                        </mwc-dialog>`
                }
                <mwc-dialog id="duplicationMapComponent" class="dialog" hideActions>
                    <modal-dialog size="md" @close="${() => this.duplicationModal.close()}">
                        <duplicate-map-component
                            slot="body"
                            .topic_id="${this.topic?._id.$oid}"
                            .topic="${this.fullTopic}"
                            @on-topic-duplicated="${() => this.duplicationModal.close()}">
                        </duplicate-map-component>
                    </modal-dialog>
                </mwc-dialog>

                <mwc-dialog id="removeTopicFromFolderModal" class="dialog" hideActions>
                    <modal-dialog size="lg" @close="${() => this.removeTopicFromFolderModal.close()}">
                        <confirm-modal
                            slot="body"
                            heading="Confirm"
                            content="Are you sure you want to remove this topic from the selected folder?"
                            cancelButtonText="Cancel"
                            submitButtonText="Confirm"
                            @confirm="${this.removeTopic}"
                            @close="${() => this.removeTopicFromFolderModal.close()}">
                        </confirm-modal>
                    </modal-dialog>
                </mwc-dialog>
                <mwc-dialog id="deleteTopicModalComponent" class="dialog" hideActions>
                    <modal-dialog size="lg" @close="${() => this.deleteTopicModalComponent?.close()}">
                        <confirm-modal
                            slot="body"
                            heading="Delete Confirm"
                            content="Are you sure to delete this map?"
                            cancelButtonText="Cancel"
                            submitButtonText="Delete"
                            @confirm="${async () => await this.deleteTopic()}"
                            @close="${() => this.deleteTopicModalComponent?.close()}">
                        </confirm-modal>
                    </modal-dialog>
                </mwc-dialog>
            `: ''}

            <mwc-dialog id="upgradeDialogComponent" class="dialog" hideActions>
                <modal-dialog size="md" @close="${() => this.upgradeDialogComponent.close()}">
                    <upgrade-dialog
                        slot="body"
                        .featureType="${FeatureType.PrintableMap}"
                        @close="${() => this.upgradeDialogComponent.close()}">
                    </upgrade-dialog>
                </modal-dialog>
            </mwc-dialog>
        `;
    }

    static get styles() {
        return css`
            .backpack-card__wrapper {
                cursor: pointer;
                width: 100%;
                position: relative;
                border-radius: 0.75rem;
                overflow: hidden;
                background: var(--theme-white-color);
                display: flex;
                border: solid .1px lightgray;
                flex-direction: column;
            }

            .backpack-card__actions {
                display: flex;
                justify-content: space-between;
                position: absolute;
                width: inherit;
                padding-top: 5px;
            }

            .backpack-card__info {
                width: 100%;
                display: flex;
                padding: 5px 5px;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                box-sizing: border-box;
            }

            .backpack-card__title-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .backpack-card__action--remove {
                margin: 6px;
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
                --mdc-theme-primary: var(--main-invalid-color);
                --mdc-theme-on-primary: var(--theme-input-color);
            }

            .backpack-card__action--add-pin {
                margin: 6px;
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
                --mdc-theme-primary: var(--main-featured-color);
                --mdc-theme-on-primary: var(--theme-input-color);
            }

            .backpack-card__action--remove-pin {
                margin: 6px;
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
                --mdc-theme-primary: white;
                --mdc-theme-on-primary: var(--theme-input-color);
            }

            .backpack-card__pin {
                padding: 10px;
            }

            .backpack-card__avatar {
                margin-left: 10px;
                border: 1px solid var(--theme-focus-border-color);
                border-radius: 50%;
                height: 40px;
                margin-top: -12px;
                margin-right: 4px;
            }

            .backpack-card__metadata {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: var(--theme-input-color);
                gap: 15px;

                /* color: var(--gray-700, #666276); */
                font-size: 12px;
                line-height: 100%;
            }

            .backpack-card__metadata-icon {
                color: var(--theme-lighter-text-color);
                padding-right: 4px;
                --mdc-icon-size: 12px;
            }

            .backpack-card__metadata-item {
                display: flex;
                align-items: center;
            }

            .backpack-card__user-type {
                padding: 3px 5px;
                height: 100%;
                align-self: center;
                font-size: 12px;
                color: var(--theme-white-color);
                border-radius: 6px;
                background: var(--main-primary-color);
                margin: 0 10px;
            }

            .backpack-card__user-type--Editor {
                background: var(--main-invalid-color);
            }

            .backpack-card__user-type--Owner {
                background: var(--main-secondary-color);
            }

            .backpack-card__image-wrapper {
                width: 100%;
                display: flex;
            }

            .backpack-card__image {
                min-width: 100%;
                min-height: 100%;
                object-fit: cover;
            }

            .shareModalBody {
                padding: 1.5rem;
            }

            .shareModalTitle {
                font-size: 1.5rem;
                font-weight: 700;
                color: var(--main-primary-color);
                text-align: center;
                margin-top: 1.5rem;
            }

            .actionButtons {
                display: flex;
                margin-top: 2rem;
                justify-content: space-between;
            }

            .tr {
                display: flex;
                border: 1px solid var(--theme-border-color);
                padding: 0.25rem 1.5rem;
                align-items: center;
            }

            a {
                text-decoration: none;
                color: inherit;
            }

            .td {
                flex: 1;
            }

            .td.td-actions {
                display: flex;
                justify-content: flex-end;
                align-items: center;
                gap: 0.25rem;
            }

            .btnIcon {
                margin-right: 0.25rem;
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }

                .backpack-card__wrapper--only {
                    max-width: 400px;
                }

                .backpack-card__image-wrapper {
                    max-height: 60px;
                }

                .backpack-card__title {
                    padding: 0px 5px;
                    margin: 0;
                    vertical-align: top;
                    font-weight: bold;
                    text-align: center;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    overflow: hidden;

                    color: #454056;
                    font-weight: 700;
                    line-height: 100%;
                }

                .small-text {
                    font-size: 0.7rem;
                    white-space: normal;
                    padding: 0;
                    font-weight: normal;
                    white-space: nowrap;
                }

            }

            @media (max-width: 550px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: block;
                }

                .tr {
                    padding: 0.5rem 1rem;
                }

                .backpack-card__title {
                    display: block;
                    margin-bottom: 8px;
                    text-align: center;
                }

                .small-text {
                    font-size: 0.8rem;
                    white-space: normal;
                    padding: 0;
                }


                .backpack-card__avatar {
                    display: block;
                    margin: auto;
                    margin-top: -20px;
                }

                .backpack-card__metadata {
                    display: none;
                }

                .backpack-card__wrapper {
                    margin-right: 10px;
                    margin-bottom: 10px;
                }

                .backpack-card__wrapper--scroll {
                    width: 200px;
                    height: 100%;
                }

                .backpack-card__wrapper--scroll .backpack-card__image-wrapper {
                    height: 130px;
                }
            }
        `;
    }
}
